import { useState, useEffect } from 'react';
import {
  Users, FileText, Calendar, MapPin, TrendingUp, AlertTriangle,
  Clock, CheckCircle, Loader2, Activity
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Stats {
  users: {
    total_users: number;
    admin_count: number;
    moderator_count: number;
    active_users: number;
    suspended_users: number;
    banned_users: number;
    new_users_30d: number;
    new_users_7d: number;
  };
  content: {
    total_posts: number;
    published_posts: number;
    hidden_posts: number;
    flagged_posts: number;
    total_events: number;
    pending_events: number;
    approved_events: number;
    total_places: number;
    unverified_places: number;
    total_stays: number;
    total_deals: number;
  };
  signupTrends: Array<{ date: string; count: number }>;
  recentActions: Array<{
    id: string;
    action: string;
    target_type: string;
    target_id: string;
    admin_name: string;
    created_at: string;
    reason?: string;
  }>;
}

function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
  color = 'coral',
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  subValue?: string;
  color?: 'coral' | 'blue' | 'green' | 'yellow' | 'red';
}) {
  const colorClasses = {
    coral: 'bg-coral-100 dark:bg-coral-900/30 text-coral-600 dark:text-coral-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          {subValue && (
            <p className="text-xs text-gray-400 dark:text-gray-500">{subValue}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionBadge({ action }: { action: string }) {
  const badges: Record<string, { label: string; className: string }> = {
    user_banned: { label: 'Banned', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    user_suspended: { label: 'Suspended', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
    user_activated: { label: 'Activated', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    role_change: { label: 'Role Changed', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    post_hidden: { label: 'Hidden', className: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
    post_published: { label: 'Published', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    post_deleted: { label: 'Deleted', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    event_approved: { label: 'Approved', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    event_rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    place_verification: { label: 'Verified', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  };

  const badge = badges[action] || { label: action, className: 'bg-gray-100 text-gray-700' };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
      {badge.label}
    </span>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem('auth_token');
      try {
        const response = await fetch(`${API_URL}/api/admin/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch stats');
        }

        const data = await response.json();
        setStats(data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Failed to load dashboard stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-coral-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 md:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Admin Dashboard
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Overview of platform activity and moderation queue
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Users}
          label="Total Users"
          value={stats?.users.total_users || 0}
          subValue={`+${stats?.users.new_users_7d || 0} this week`}
          color="blue"
        />
        <StatCard
          icon={FileText}
          label="Total Posts"
          value={stats?.content.total_posts || 0}
          subValue={`${stats?.content.flagged_posts || 0} flagged`}
          color="coral"
        />
        <StatCard
          icon={Calendar}
          label="Events"
          value={stats?.content.total_events || 0}
          subValue={`${stats?.content.pending_events || 0} pending approval`}
          color="green"
        />
        <StatCard
          icon={MapPin}
          label="Places"
          value={stats?.content.total_places || 0}
          subValue={`${stats?.content.unverified_places || 0} unverified`}
          color="yellow"
        />
      </div>

      {/* Moderation Queue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Pending Items */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Moderation Queue
          </h2>
          <div className="space-y-3">
            {(stats?.content.pending_events || 0) > 0 && (
              <a
                href="/admin/events?status=pending"
                className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-gray-900 dark:text-white">Pending Events</span>
                </div>
                <span className="px-2.5 py-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 rounded-full text-sm font-medium">
                  {stats?.content.pending_events}
                </span>
              </a>
            )}

            {(stats?.content.flagged_posts || 0) > 0 && (
              <a
                href="/admin/posts?flagged=true"
                className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                  <span className="text-gray-900 dark:text-white">Flagged Posts</span>
                </div>
                <span className="px-2.5 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full text-sm font-medium">
                  {stats?.content.flagged_posts}
                </span>
              </a>
            )}

            {(stats?.content.unverified_places || 0) > 0 && (
              <a
                href="/admin/places?verified=false"
                className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-gray-900 dark:text-white">Unverified Places</span>
                </div>
                <span className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                  {stats?.content.unverified_places}
                </span>
              </a>
            )}

            {(stats?.content.pending_events || 0) === 0 &&
             (stats?.content.flagged_posts || 0) === 0 &&
             (stats?.content.unverified_places || 0) === 0 && (
              <div className="flex items-center gap-3 p-3 text-green-600 dark:text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span>All caught up! No pending items.</span>
              </div>
            )}
          </div>
        </div>

        {/* User Stats */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-coral-500" />
            User Statistics
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Active Users</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {stats?.users.active_users || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Admins</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {stats?.users.admin_count || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Moderators</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {stats?.users.moderator_count || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Suspended</span>
              <span className="font-semibold text-yellow-600 dark:text-yellow-400">
                {stats?.users.suspended_users || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Banned</span>
              <span className="font-semibold text-red-600 dark:text-red-400">
                {stats?.users.banned_users || 0}
              </span>
            </div>
            <div className="pt-3 border-t dark:border-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">New (30 days)</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  +{stats?.users.new_users_30d || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Signup Trend Chart */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-coral-500" />
          User Signups (Last 30 Days)
        </h2>
        {stats?.signupTrends && stats.signupTrends.length > 0 ? (
          <div className="h-32 flex items-end gap-1">
            {stats.signupTrends.map((day, i) => {
              const maxCount = Math.max(...stats.signupTrends.map(d => d.count));
              const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
              return (
                <div
                  key={day.date}
                  className="flex-1 group relative"
                >
                  <div
                    className="bg-coral-500 rounded-t transition-all hover:bg-coral-600"
                    style={{ height: `${Math.max(height, 4)}%` }}
                  />
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {day.count}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            No signup data available
          </p>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-coral-500" />
          Recent Moderation Activity
        </h2>
        {stats?.recentActions && stats.recentActions.length > 0 ? (
          <div className="space-y-3">
            {stats.recentActions.map((action) => (
              <div
                key={action.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <ActionBadge action={action.action} />
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white">
                      <span className="font-medium">{action.admin_name}</span>
                      {' '}on {action.target_type} #{action.target_id.slice(0, 8)}
                    </p>
                    {action.reason && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Reason: {action.reason}
                      </p>
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {new Date(action.created_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            No recent moderation activity
          </p>
        )}
      </div>
    </div>
  );
}
